// ---------------------------------------------------------------------------
// routeDiagnostics — debug-only types + helpers for the route screen overlay.
//
// Temporary debugging tool to surface what's actually happening in the
// route generation pipeline. Each RawRoute gets tagged with __source as
// it's created. After classifyThreeCards picks the three cards, we build
// a RouteDiagnostics object that's stored in component state and rendered
// by the DebugOverlay component on the route screen.
//
// Delete this file (and references) when the loop/dead-end debugging is
// done. Grep for ROUTE_DIAGNOSTICS to find all touch points.
// ---------------------------------------------------------------------------

import type { RawRoute } from "@/api/routing";

/** Tag a route with its source. Mutating the route object is intentional
 *  — these tags need to ride along through every async boundary without
 *  threading separate metadata.
 *
 *  ENUMERABLE ON PURPOSE (release spec item 1, Aug 11). This was
 *  enumerable:false "to keep serialization outputs clean", and that is the
 *  exact property spread copying skips — so every `{ ...route }` along the
 *  canonical-Fastest install path silently produced src=unknown, and the
 *  card lost the one field that says who built the route. A tag that dies
 *  on spread is how a 100%-motorway route got relabelled by absence. The
 *  cost is a `__source` key visible in JSON output, which is harmless and,
 *  during a bug hunt, actively useful. */
export function tagRouteSource(route: RawRoute, source: string): void {
  Object.defineProperty(route, "__source", {
    value: source,
    enumerable: true,
    configurable: true,
    writable: true,
  });
}

/** Read a route's source tag. Returns "unknown" when the route wasn't
 *  tagged (older code paths, future regressions, etc.). */
export function getRouteSource(route: RawRoute): string {
  const tagged = (route as RawRoute & { __source?: string }).__source;
  return typeof tagged === "string" ? tagged : "unknown";
}

/** Tag every route in an array with the same source. */
export function tagRoutesSource(routes: RawRoute[], source: string): void {
  for (const r of routes) tagRouteSource(r, source);
}

/** Per-slot summary of which route won and what it looks like. */
export interface SlotDiagnostics {
  source: string;
  distanceMi: number;
  durationMin: number;
  highwayPct: number;
  pleasantScore: number;
  /** True when the card has collapsed/sameAsClass set — the slot's pick
   *  isn't a distinct route, it's pointing at another slot. */
  collapsed: boolean;
}

/** Top-level diagnostics rendered by the overlay. */
export interface RouteDiagnostics {
  /** Total routes in the pool after dedup. */
  poolSize: number;
  /** Per-source counts: "mapbox-direct" → 3, "valhalla-balanced" → 2, etc. */
  sourceCounts: Record<string, number>;
  /** Picks for each slot. */
  direct: SlotDiagnostics | null;
  pleasant: SlotDiagnostics | null;
  adventure: SlotDiagnostics | null;
  /** Timestamp the diagnostics were built — for "did it refresh" checks. */
  builtAtMs: number;
}
